import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Eye, ArrowRight } from 'lucide-react';
import { marketingData } from '@/data/marketingData';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function Marketing() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const totalPages = Math.ceil(marketingData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = marketingData.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        {/* Banner */}
        <section className="relative h-[300px] md:h-[400px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=1920&h=600&fit=crop)` }}
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative h-full section-container flex items-center">
            <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">营销中心</h1>
              <p className="text-xl text-white/80">精准营销解决方案，助力企业数字化增长</p>
            </div>
          </div>
        </section>

        {/* Content List */}
        <section ref={sectionRef} className="py-16 bg-gray-50">
          <div className="section-container">
            {/* Breadcrumb */}
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-gray-500">
                <li><Link to="/" className="hover:text-[#1c5bff] transition-colors">首页</Link></li>
                <li>/</li>
                <li className="text-black font-medium">营销中心</li>
              </ol>
            </nav>

            {/* Items Grid */}
            <div className="space-y-6">
              {paginatedItems.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/marketing/${item.id}`}
                  className={`group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{
                    transitionTimingFunction: 'var(--ease-expo-out)',
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3 lg:w-1/4 h-48 md:h-auto overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
                      <div>
                        <h2 className="text-xl md:text-2xl font-bold text-black mb-3 group-hover:text-[#1c5bff] transition-colors">
                          {item.title}
                        </h2>
                        <p className="text-gray-600 leading-relaxed line-clamp-3 mb-4">
                          {item.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {item.features.map((feature, i) => (
                            <span key={i} className="px-3 py-1 bg-[#1c5bff]/10 text-[#1c5bff] text-sm rounded-full">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            更新时间：{item.updateTime}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {item.views.toLocaleString()} 次浏览
                          </span>
                        </div>
                        <span className="flex items-center gap-1 text-[#1c5bff] font-medium">
                          查看详情
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all duration-300 ${
                      page === currentPage
                        ? 'bg-[#1c5bff] text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1c5bff] hover:border-[#1c5bff] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
            <p className="text-center text-gray-500 text-sm mt-4">
              共 {marketingData.length} 条内容，当前第 {currentPage}/{totalPages} 页
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
